import { supabase } from '../../lib/supabase';
import { EXERCISE_SELECT_COLUMNS, type ExerciseEquipmentRow, type ExerciseMediaRow, type ExerciseRow } from '../../lib/types';
import type { EquipmentRow } from '../../lib/types';
import { HOW_TO_COLUMNS, type HowToRow } from '../../lib/howTo';

/** Everything the list page needs, fetched in a few batched queries and merged client-side (library is small: a few hundred rows at most). */
export type ExerciseLibraryData = {
  exercises: ExerciseRow[];
  media: ExerciseMediaRow[];
  equipmentLinks: ExerciseEquipmentRow[];
  equipment: EquipmentRow[];
};

export async function loadExerciseLibrary(): Promise<ExerciseLibraryData> {
  const [exercisesRes, mediaRes, linksRes, equipmentRes] = await Promise.all([
    supabase.from('exercises').select(EXERCISE_SELECT_COLUMNS).is('owner_id', null).order('name_en', { ascending: true }),
    supabase.from('exercise_media').select('id, exercise_id, kind, rendition, path, sort, title_en, title_he, poster_path, created_at, updated_at'),
    supabase.from('exercise_equipment').select('exercise_id, equipment_id, sort'),
    supabase.from('equipment').select('id, name_en, name_he, category, class, photo_path, sort, archived_at, created_at, updated_at').order('sort'),
  ]);
  if (exercisesRes.error) throw exercisesRes.error;
  if (mediaRes.error) throw mediaRes.error;
  if (linksRes.error) throw linksRes.error;
  if (equipmentRes.error) throw equipmentRes.error;

  return {
    exercises: (exercisesRes.data ?? []) as unknown as ExerciseRow[],
    media: (mediaRes.data ?? []) as ExerciseMediaRow[],
    equipmentLinks: (linksRes.data ?? []) as ExerciseEquipmentRow[],
    equipment: (equipmentRes.data ?? []) as EquipmentRow[],
  };
}

export async function getExercise(id: string): Promise<ExerciseRow | null> {
  const { data, error } = await supabase.from('exercises').select(EXERCISE_SELECT_COLUMNS).eq('id', id).maybeSingle();
  if (error) throw error;
  return (data as unknown as ExerciseRow) ?? null;
}

export async function getExerciseMedia(exerciseId: string): Promise<ExerciseMediaRow[]> {
  const { data, error } = await supabase
    .from('exercise_media')
    .select('id, exercise_id, kind, rendition, path, sort, title_en, title_he, poster_path, created_at, updated_at')
    .eq('exercise_id', exerciseId)
    .order('sort', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ExerciseMediaRow[];
}

export async function getExerciseEquipment(exerciseId: string): Promise<ExerciseEquipmentRow[]> {
  const { data, error } = await supabase.from('exercise_equipment').select('exercise_id, equipment_id, sort').eq('exercise_id', exerciseId).order('sort', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ExerciseEquipmentRow[];
}

export type ExerciseInput = {
  name_en: string;
  name_he: string;
  muscle_group: string;
  equipment: string;
  kind: ExerciseRow['kind'];
  increment_kg: number;
  sport: ExerciseRow['sport'];
  load_mode: ExerciseRow['load_mode'];
  track_reps: boolean;
  track_weight: boolean;
  track_time: boolean;
  track_distance: boolean;
  track_rir: boolean;
  description_en: string | null;
  description_he: string | null;
  muscles: string[];
};

export async function createExercise(input: ExerciseInput): Promise<string> {
  const { data, error } = await supabase
    .from('exercises')
    .insert({ ...input, owner_id: null, updated_at: new Date().toISOString() })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function updateExercise(id: string, patch: Partial<ExerciseInput>): Promise<void> {
  const { error } = await supabase
    .from('exercises')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Wave 16d: the exercise screen's how-to (migration 0045). */
export async function getExerciseHowTo(id: string): Promise<HowToRow | null> {
  const { data, error } = await supabase.from('exercises').select(HOW_TO_COLUMNS).eq('id', id).maybeSingle();
  if (error) throw error;
  return (data as HowToRow | null) ?? null;
}

export async function updateExerciseHowTo(id: string, patch: HowToRow): Promise<void> {
  const { error } = await supabase
    .from('exercises')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function setExerciseArchived(id: string, archived: boolean): Promise<void> {
  const { error } = await supabase
    .from('exercises')
    .update({ archived_at: archived ? new Date().toISOString() : null, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Replaces the exercise's equipment links wholesale (delete + reinsert), in the given sort order. */
export async function setExerciseEquipment(exerciseId: string, equipmentIds: string[]): Promise<void> {
  const { error: delError } = await supabase.from('exercise_equipment').delete().eq('exercise_id', exerciseId);
  if (delError) throw delError;
  if (equipmentIds.length === 0) return;
  const rows = equipmentIds.map((equipment_id, sort) => ({ exercise_id: exerciseId, equipment_id, sort }));
  const { error: insError } = await supabase.from('exercise_equipment').insert(rows);
  if (insError) throw insError;
}

export async function insertMediaRow(row: {
  exercise_id: string;
  kind: 'demo_photo' | 'video' | 'image';
  rendition: string;
  path: string;
  sort: number;
  title_en?: string | null;
  title_he?: string | null;
  poster_path?: string | null;
  mime?: string | null;
  width?: number | null;
  height?: number | null;
  duration_s?: number | null;
  bytes?: number | null;
}): Promise<string> {
  const { data, error } = await supabase
    .from('exercise_media')
    .insert({ ...row, updated_at: new Date().toISOString() })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function updateMediaRow(
  id: string,
  patch: Partial<{
    title_en: string | null;
    title_he: string | null;
    sort: number;
    path: string;
    poster_path: string | null;
    mime: string | null;
    bytes: number | null;
    width: number | null;
    height: number | null;
  }>,
): Promise<void> {
  const { error } = await supabase
    .from('exercise_media')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteMediaRow(id: string): Promise<void> {
  const { error } = await supabase.from('exercise_media').delete().eq('id', id);
  if (error) throw error;
}
